import React, { useEffect, useMemo, useState } from 'react';

import { Box, Button, Chip, ChipPropsColorOverrides, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridFilterModel, GridPaginationModel } from '@mui/x-data-grid';
import { Link, useNavigate } from 'react-router-dom';

import { HelpDeskStatusType } from '../..';
import api from '../../../../../services/api';
import ProfileTooltip from '../../../../../components/ProfileTooltip';
import { avatarUrl, toNumber } from '../../../../../utils/functions';
import { useUrlQuery } from '../../../../../hooks/useUrlQuery';

interface IHelpdeskRequests {}

type HelpdeskRequestType = {
  current_page: number;
  data_limit: number;
  order_dir: string;
  order_by: string;
  filter_field: string;
  filter_operator: string;
  filter_value: string;
  data_total: number;
  total_page: number;
  data: HelpdeskRequestDataType[];
};

type HelpdeskRequestDataType = {
  id: string;
  target_id: string;
  target_description: string;
  task_id: string;
  task_description: string;
  created_by: string;
  created_by_nickname: string;
  title: string;
  status: string;
  created_at: string;
  created_at_date: string;
  created_at_time: string;
  expected_end: string;
  expected_end_date: string;
  attendant_user_id: string;
  attendant_user_nickname: string;
};
type FilterModeItemType = {
  field: string;
  operator: string;
  value: string;
  type: string;
};
type FilterModelType = {
  items: FilterModeItemType[];
};
function RenderStatus(status: HelpDeskStatusType) {
  const isLate = status?.includes('_late');

  const label = {
    canceled: 'Cancelado',
    canceled_late: 'Cancelado',
    ended: 'Finalizado',
    ended_late: 'Finalizado',
    not_started: 'Não iniciado',
    not_started_late: 'Não iniciado',
    started: 'Andamento',
    started_late: 'Andamento',
  };

  const color: ChipPropsColorOverrides = {
    canceled: 'error',
    canceled_late: 'error',
    ended: 'success',
    ended_late: 'success',
    not_started: 'warning',
    not_started_late: 'warning',
    started: 'info',
    started_late: 'info',
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <Chip size="small" label={label[status]} variant="outlined" color={color[status]} />
      {isLate ? (
        <Chip size="small" label="Atrasado" variant="outlined" color="error" />
      ) : (
        <Chip size="small" label="No prazo" variant="outlined" color="success" />
      )}
    </Box>
  );
}

function HelpdeskRequests() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const urlQuery = useUrlQuery();
  const navigate = useNavigate();
  // const currentUrlView = urlQuery.get('view') ?? 'dashboard';
  const currentUrlFilter = urlQuery.get('filter') ?? 'all';
  // const currentUrlPage = urlQuery.get('page') ?? '1';

  const [isFetching, setIsFetching] = useState(false);
  const [data, setData] = React.useState<HelpdeskRequestType>({
    current_page: 1,
    data_limit: 10,
    order_dir: 'asc',
    order_by: 'id',
    filter_field: '',
    filter_operator: '',
    filter_value: '',
    data_total: 0,
    total_page: 1,
    data: [],
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: toNumber(urlQuery.get('current_page') ?? '0'),
    pageSize: toNumber(urlQuery.get('data_limit') ?? '15'),
  });

  const [filterModel, setFilterModel] = React.useState<FilterModelType | GridFilterModel>({
    items: [
      {
        field: urlQuery.get('filter_field') ?? '',
        operator: urlQuery.get('filter_operator') ?? '',
        value: urlQuery.get('filter_value') ?? '',
        type: urlQuery.get('filter_type') ?? '',
      },
    ],
  });

  const [sortModel, setSortModel] = React.useState([
    { field: urlQuery.get('order_by') ?? 'id', sort: urlQuery.get('order_dir') ?? 'asc' },
  ]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Nº',
      width: 90,
      type: 'number',
      renderCell: params => (
        <Tooltip title="Visualizar Helpdesk" disableInteractive arrow>
          <Button component={Link} to={`/helpdesk/${params?.value}`} target="_blank" rel="noopener noreferrer">
            {params?.value}
          </Button>
        </Tooltip>
      ),
    },
    { field: 'target_description', headerName: 'Setor', width: 160 },
    { field: 'task_description', headerName: 'Tarefa', minWidth: 160, width: 240 },
    {
      field: 'created_at',
      headerName: 'Data Criação',
      type: 'date',
      width: 160,
      valueGetter: ({ value }) => value && new Date(value),
      renderCell: params => {
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
            {`${params.row.created_at_date} ${params.row.created_at_time} `}
          </Box>
        );
      },
    },
    {
      field: 'created_by',
      headerName: 'Solicitante',
      width: 200,
      renderCell: params => {
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
            {params.row.created_by_nickname ? (
              <ProfileTooltip userId={params?.value.toString()} openNewTab>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                    }}
                    src={avatarUrl(params.value)}
                    alt={params.row.created_by_nickname || params.value}
                    onError={(e: any) => {
                      e.target.src = avatarUrl(0, true);
                    }}
                  />
                  <Typography color={theme.palette.text.primary} sx={{ textDecoration: 'none' }} variant="inherit">
                    {params.row.created_by_nickname || ' '}
                  </Typography>
                </Box>
              </ProfileTooltip>
            ) : (
              <Typography color={theme.palette.text.primary} sx={{ textDecoration: 'none' }} variant="inherit">
                Sem atendente
              </Typography>
            )}
          </Box>
        );
      },
    },
    { field: 'title', headerName: 'Assunto', flex: 1 },

    {
      field: 'attendant_user_id',
      headerName: 'Atendente',
      width: 200,
      renderCell: params => {
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
            {params.row.attendant_user_nickname ? (
              <ProfileTooltip userId={params?.value.toString()} openNewTab>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                    }}
                    src={avatarUrl(params.value)}
                    alt={params.row.attendant_user_nickname || params.value}
                    onError={(e: any) => {
                      e.target.src = avatarUrl(0, true);
                    }}
                  />
                  <Typography color={theme.palette.text.primary} sx={{ textDecoration: 'none' }} variant="inherit">
                    {params.row.attendant_user_nickname || ' '}
                  </Typography>
                </Box>
              </ProfileTooltip>
            ) : (
              <Typography color={theme.palette.text.primary} sx={{ textDecoration: 'none' }} variant="inherit">
                Sem atendente
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'expected_end',
      headerName: 'Prazo',
      type: 'date',
      width: 160,
      valueGetter: ({ value }) => value && new Date(value),
      renderCell: params => {
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
            {`${params.row.expected_end_date}`}
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Situação',
      width: 200,
      renderCell: params => {
        if (!params.value) {
          return '';
        }
        return RenderStatus(params.value);
      },
    },
  ];

  const fetcher = async () => {
    try {
      if (isFetching) return;
      setIsFetching(true);

      const params = {
        current_page: paginationModel.page.toString(),
        data_limit: paginationModel.pageSize.toString(),
        order_by: sortModel?.[0]?.field || 'id',
        order_dir: sortModel?.[0]?.sort || 'asc',
        filter_field: filterModel?.items?.[0]?.field || 'id',
        filter_operator: filterModel?.items?.[0]?.operator || '=',
        filter_value: filterModel?.items?.[0]?.value || '',
        filter_type: columns.find(c => c.field === filterModel?.items?.[0]?.field)?.type || 'string',
      };

      const urlSearchParams = new URLSearchParams({
        view: 'requests',
        filter: currentUrlFilter,
        ...params,
      });

      navigate({ search: urlSearchParams?.toString() });

      const { data: helpdesk } = await api.get<HelpdeskRequestType>('v2/helpdesk/list/requests', {
        params,
      });

      setData(helpdesk);
    } catch (error) {
      console.error(error);
    }
    setIsFetching(false);
  };

  useEffect(() => {
    fetcher();
  }, [paginationModel, sortModel, filterModel]);

  useEffect(() => {
    if (currentUrlFilter === 'started') {
      setFilterModel({ items: [{ field: 'status', operator: 'startsWith', type: 'string', value: 'started' }] });
    } else if (currentUrlFilter === 'not_started') {
      setFilterModel({ items: [{ field: 'status', operator: 'startsWith', type: 'string', value: 'not_started' }] });
    } else if (currentUrlFilter === 'lated') {
      setFilterModel({ items: [{ field: 'status', operator: 'contains', type: 'string', value: 'lated' }] });
    } else if (currentUrlFilter === 'ended') {
      setFilterModel({ items: [{ field: 'status', operator: 'contains', type: 'string', value: 'ended' }] });
    } else {
      setFilterModel({ items: [] });
    }
  }, [currentUrlFilter]);

  const renderDataGrid = useMemo(() => {
    return (
      <DataGrid
        rows={data?.data || []}
        columns={columns}
        pagination
        sortingMode="server"
        filterMode="server"
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        // onSortModelChange={setSortModel}
        onFilterModelChange={setFilterModel}
        filterModel={filterModel}
        loading={isFetching}
        rowCount={data?.data_total || 0}
        paginationModel={paginationModel}
        disableRowSelectionOnClick
      />
    );
  }, [data?.data, isFetching]);
  return (
    <Box>
      <Box sx={{ height: 'calc(100vh - 174px)', width: '100%' }}>{renderDataGrid}</Box>
    </Box>
  );
}

export { HelpdeskRequests };
